import { InjectionToken, inject, signal, computed, Injector } from '@angular/core';
import { isJSType, assign, deepCopy, isEqualJSON, isNotNullish, selectObjectProps, recordObject, fillObject, makeReadonlySignal } from '@noda-lib/utils/handlers';
import { HttpClient } from '@angular/common/http';
import { Subject, takeUntil, finalize, BehaviorSubject, filter, take, firstValueFrom } from 'rxjs';
import { makeTagsURL } from '@noda-lib/utils/http';
import { distinctUntilChanged, map, filter as filter$1, take as take$1 } from 'rxjs/operators';
const ROOT_HTTP_STORE = new InjectionToken('ROOT_HTTP_STORE');

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * @deprecated use @noda-lib/requests
 */
function useHttpRootStore() {
  return inject(ROOT_HTTP_STORE);
}
/**
 * @deprecated use @noda-lib/requests
 */
function useHttpStore(Store) {
  return inject(Store);
}

/**
 * @deprecated use @noda-lib/requests
 */
class RequestMeta {
  constructor(urlOrSettings) {
    this.settings = null;
    const url = isJSType(urlOrSettings, 'string') ? {
      url: urlOrSettings
    } : urlOrSettings;
    const settings = assign({
      lazy: false,
      initialValue: null,
      options: null
    }, url);
    this.settings = settings;
  }
}

/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
class HttpStateItem {
  constructor(initialMeta, http) {
    this.initialMeta = initialMeta;
    this.http = http;
    this.meta = null;
    this.initiated = false;
    this.loaded = false;
    this.loading = signal(false);
    this.error = signal(null);
    this.value = signal(this.initialMeta.settings.initialValue);
    this.rxValue$ = null;
    this.abort$ = new Subject();
    this.abort = () => this.abort$.next();
    this.update = options => {
      this.abort$.next();
      this.setOptions(options);
      this.sendRequest(this.meta.settings.url, options);
    };
    this.resetMeta();
  }
  resetMeta() {
    this.meta = deepCopy(this.initialMeta);
  }
  reset() {
    this.initiated = false;
    this.loaded = false;
    this.loading.set(false);
    this.error.set(null);
    this.resetMeta();
    this.setValue(this.meta.settings.initialValue);
  }
  sendRequest(url = this.meta.settings.url, options = this.meta.settings.options) {
    url = makeTagsURL(url, options?.query, options?.params);
    this.abort();
    this.loading.set(true);
    this.initiated = true;
    const error = err => this.error.set(err);
    const next = response => {
      this.loaded = true;
      this.error.set(null);
      this.setValue(response);
    };
    this.http.get(url).pipe(takeUntil(this.abort$), finalize(() => this.loading.set(false))).subscribe({
      next,
      error
    });
  }
  sendRequestIfCan() {
    const {
      lazy
    } = this.meta.settings;
    if (!this.initiated && !lazy) this.sendRequest();
  }
  setOptions(options, settings) {
    const newMeta = deepCopy(this.initialMeta);
    newMeta.settings.options = options;
    this.meta = newMeta;
    if (settings?.sendRequest) this.sendRequest();
  }
  setValue(value) {
    this.value.set(value);
    this.rxValue$?.next(value);
  }
  createField(value) {
    return {
      value,
      error: this.error,
      loading: this.loading.asReadonly(),
      abort: this.abort,
      update: this.update
    };
  }
  // * Rx
  getRxField() {
    this.checkAndCreateRxValue();
    this.sendRequestIfCan();
    return this.createField(this.rxValue$.asObservable());
  }
  checkAndCreateRxValue() {
    if (!this.rxValue$) this.rxValue$ = new BehaviorSubject(this.value());
  }
  selectFirstLoadedValue() {
    this.checkAndCreateRxValue();
    if (!this.initiated) this.sendRequest();
    return this.rxValue$.pipe(filter(_ => this.loaded), take(1));
  }
  // * Signal
  getSignalField() {
    this.sendRequestIfCan();
    return this.createField(this.value.asReadonly());
  }
}

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/prefer-reduce-type-parameter */
/**
 * @deprecated use @noda-lib/requests
 */
class HttpStore {
  constructor(storeConstructor, injector) {
    this.storeConstructor = storeConstructor;
    this.injector = injector;
    this.storeState = null;
    this.http = null;
    this.initStore();
  }
  initStore() {
    this.http = this.injector.get(HttpClient);
    this.createStoreState(new this.storeConstructor());
  }
  createStoreState(storeInstance) {
    this.storeState = Object.entries(storeInstance).reduce((accum, [key, meta]) => {
      accum[key] = new HttpStateItem(meta, this.http);
      return accum;
    }, {});
  }
  getValues(keys) {
    return keys.reduce((accum, key) => {
      accum[key] = this.getValue(key);
      return accum;
    }, {});
  }
  getValue(...keys) {
    return keys.length === 1 ? this.storeState[keys[0]].value() : this.getValues(keys);
  }
  setValue(key, value) {
    this.storeState[key].setValue(value);
  }
  patchStore(patchValue) {
    Object.entries(patchValue).forEach(([key, value]) => {
      this.setValue(key, value);
    });
  }
  setOptions(key, options, settings) {
    this.storeState[key].setOptions(options, settings);
  }
  getFirstLoadedValue(key, mode = 'observer') {
    const firstValue$ = this.storeState[key].selectFirstLoadedValue();
    return mode === 'observer' ? firstValue$ : firstValueFrom(firstValue$);
  }
  selectRx(key, options) {
    const field = this.storeState[key].getRxField();
    const result = options?.onlyValue ? field.value : field;
    return result;
  }
  select(key, options) {
    const field = this.storeState[key].getSignalField();
    const isOnlyValue = Boolean(options?.onlyValue);
    const isExistTransform = Boolean(options?.transform);
    const isFullOptions = isOnlyValue && isExistTransform;
    switch (true) {
      case isFullOptions:
        {
          const {
            value
          } = field;
          return computed(() => options.transform(value()));
        }
      case isOnlyValue:
        {
          return field.value;
        }
      case isExistTransform:
        {
          const {
            value
          } = field;
          field.value = computed(() => options.transform(value()));
          return field;
        }
      default:
        {
          return field;
        }
    }
  }
  reset() {
    Object.keys(this.storeState).forEach(key => {
      this.storeState[key].reset();
    });
  }
  resetFields(fields) {
    fields.forEach(key => this.storeState[key].reset());
  }
}

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * @deprecated use @noda-lib/requests
 */
function provideRootHttpStore(StoreConstructor) {
  return {
    provide: ROOT_HTTP_STORE,
    useFactory() {
      const injector = inject(Injector);
      const store = new HttpStore(StoreConstructor, injector);
      return store;
    }
  };
}
/**
 * @deprecated use @noda-lib/requests
 */
function provideHttpStore(Store) {
  return [{
    provide: Store,
    useFactory() {
      const injector = inject(Injector);
      const store = new HttpStore(Store, injector);
      return store;
    }
  }];
}

/* eslint-disable @typescript-eslint/naming-convention */
/**
 *
 * @deprecated use useStore
 */
function RxStoreToken(storeName) {
  return new InjectionToken(`RX-STORE:${storeName}`);
}
/**
 *
 * @deprecated use useRootStore
 */
function useRxRootStore() {
  return inject(ROOT_RX_STORE);
}
/**
 *
 * @deprecated use useStore
 */
function useRxStore(Store) {
  return inject(Store);
}

/**
 * @deprecated use Store
 */
class RxStore {
  constructor(StoreConstructor) {
    this.StoreConstructor = StoreConstructor;
    this.store$ = new BehaviorSubject(null);
    this.stateChanges = this.initStoreChangesSub();
    this.store$.next(new StoreConstructor());
  }
  initStoreChangesSub() {
    return this.store$.asObservable().pipe(distinctUntilChanged(this.isEqualValues));
  }
  isEqualValues(previous, current) {
    const isEqualValues = isEqualJSON(previous, current);
    return isEqualValues;
  }
  getStore() {
    return this.store$.getValue();
  }
  get DefaultStore() {
    return new this.StoreConstructor();
  }
  getValue(key) {
    return this.store$.getValue()[key];
  }
  listen() {
    return this.store$.asObservable();
  }
  select(key) {
    return this.store$.asObservable().pipe(map(store => store[key]), distinctUntilChanged(this.isEqualValues));
  }
  selectFirstDefinedValue(key, mode = 'default') {
    const stream$ = this.store$.asObservable().pipe(map(store => store[key]), filter$1(isNotNullish));
    const value = mode === 'default' ? stream$.pipe(take$1(1)) : firstValueFrom(stream$);
    return value;
  }
  /**
   * @deprecated use method patchStore
   */
  updateStore(value) {
    this.patchStore(value);
  }
  /**
   * @deprecated use method setValue
   */
  mergeStore(key, value) {
    this.setValue(key, value);
  }
  // * New renamed methods
  patchStore(partialStore) {
    const currentStore = this.getStore();
    const updatedStore = Object.assign({}, currentStore, partialStore);
    this.store$.next(updatedStore);
  }
  setValue(key, value) {
    this.patchStore({
      [key]: value
    });
  }
  reset(emitEvent = true) {
    const Store = this.getStore();
    Object.assign(Store, this.DefaultStore);
    if (emitEvent) this.patchStore(Store);
  }
  resetFields(fields, options) {
    const isNonNullable = options?.nonNullable;
    if (isNonNullable) {
      const resetObject = selectObjectProps(this.DefaultStore, fields);
      this.patchStore(resetObject);
    } else {
      const resetObject = recordObject(fields, null);
      this.patchStore(resetObject);
    }
  }
}

//  * Exports
/**
 * @deprecated use useRootStore
 */
const ROOT_RX_STORE = new InjectionToken('ROOT_RX_STORE');

/**
 * @deprecated use provideRootStore
 */
function provideRxRootStore(Store) {
  return {
    provide: ROOT_RX_STORE,
    useValue: new RxStore(Store)
  };
}
/**
 *
 * @deprecated use provideStore
 */
function provideRxChildStore(storeToken, Store) {
  return [{
    provide: storeToken,
    useValue: new RxStore(Store)
  }];
}
/**
 *
 * @deprecated use provideStore
 */
function provideRxStore(Store) {
  return {
    provide: Store,
    useValue: new RxStore(Store)
  };
}

/**
 * @deprecated use useRootStore
 */
const ROOT_SIGNAL_STORE = new InjectionToken('ROOT_SIGNAL_STORE');

/* eslint-disable @typescript-eslint/naming-convention */
/**
 *
 * @deprecated use useStore
 */
function SignalStoreToken(storeName) {
  return new InjectionToken(`SIGNAL-STORE:${storeName}`);
}
/**
 *
 * @deprecated use useRootStore
 */
function useSignalRootStore() {
  return inject(ROOT_SIGNAL_STORE);
}
/**
 *
 * @deprecated use useStore
 */
function useSignalStore(Store) {
  return inject(Store);
}
const DEFAULT_RESET_SIGNAL_OPTIONS = {
  nonNullable: true
};
function makeReadonlyStore(store) {
  const storeWithReadonlySignals = Object.entries(store).reduce((accum, [key, value]) => {
    accum[key] = value.asReadonly();
    return accum;
  }, {});
  return Object.freeze(storeWithReadonlySignals);
}

/* eslint-disable @typescript-eslint/prefer-reduce-type-parameter */
/**
 * @deprecated use Store
 */
class SignalStore {
  constructor(StoreConstructor) {
    this.StoreConstructor = StoreConstructor;
    this.store = null;
    this.initStore();
  }
  initStore() {
    const instance = new this.StoreConstructor();
    const store = Object.entries(instance).reduce((accum, [key, value]) => {
      accum[key] = signal(value);
      return accum;
    }, {});
    this.store = Object.freeze(store);
  }
  getInstance(options) {
    const instance = options?.nonNullable ? new this.StoreConstructor() : fillObject(new this.StoreConstructor(), null);
    return instance;
  }
  getStore(type = 'readonly') {
    const store = type === 'writable' ? this.store : makeReadonlyStore(this.store);
    return store;
  }
  getValue(key) {
    return this.store[key]();
  }
  setValue(key, value) {
    this.store[key].set(value);
  }
  patchStore(store) {
    Object.entries(store).forEach(([key, value]) => this.setValue(key, value));
  }
  select(key, options) {
    const isExistTransform = Boolean(options?.transform);
    return isExistTransform ? computed(() => options.transform(this.store[key]())) : makeReadonlySignal(this.store[key]);
  }
  reset(options) {
    const resetOptions = assign(DEFAULT_RESET_SIGNAL_OPTIONS, options);
    const instance = this.getInstance(resetOptions);
    Object.entries(instance).forEach(([key, value]) => this.setValue(key, value));
  }
  resetFields(fields, options) {
    const instance = this.getInstance(options);
    fields.forEach(field => this.setValue(field, instance[field]));
  }
}

/**
 * @deprecated use provideRootStore
 */
function provideSignalRootStore(store) {
  return {
    provide: ROOT_SIGNAL_STORE,
    useValue: new SignalStore(store)
  };
}
/**
 *
 * @deprecated use provideStore
 */
function provideChildSignalStore(storeToken, store) {
  return {
    provide: storeToken,
    useValue: new SignalStore(store)
  };
}
/**
 *
 * @deprecated use provideStore
 */
function provideSignalStore(Store) {
  return {
    provide: Store,
    useValue: new SignalStore(Store)
  };
}

/**
 * Generated bundle index. Do not edit.
 */

export { HttpStore, ROOT_RX_STORE, ROOT_SIGNAL_STORE, RequestMeta, RxStore, RxStoreToken, SignalStore, SignalStoreToken, provideChildSignalStore, provideHttpStore, provideRootHttpStore, provideRxChildStore, provideRxRootStore, provideRxStore, provideSignalRootStore, provideSignalStore, useHttpRootStore, useHttpStore, useRxRootStore, useRxStore, useSignalRootStore, useSignalStore };
